import React from "react";
import { isQrChat, hasMetaServiceWindow } from "../../utils/platformFamily";
import IconButtonWithTooltip from "./Shared/IconButtonWithTooltip";
import GroupAdminActions from "./GroupAdminActions";
import useWhatsappWebStatus from "../../hooks/useWhatsappWebStatus";
import WhatsAppWebLinkActions from "./Features/WhatsAppWebLinkActions";
import {
  Box,
  IconButton,
  Typography,
  Collapse,
  useTheme,
  TextField,
  Chip,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  Snackbar,
  Alert,
  Divider,
  Avatar,
  Switch,
  Skeleton,
} from "@mui/material";
import {
  ProfileCircle,
  Clock,
  ShoppingBag,
  SidebarRight as SidebarToggleIconIx,
  Edit2,
  ArrowDown2,
  TickCircle,
  TickSquare,
  CloseSquare,
  Trash,
  Add,
  MessageText,
  DocumentText1,
  DocumentText,
  Gallery,
  VideoSquare,
  MusicSquare,
} from "iconsax-react";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  selectContactInfo,
  selectInboxInfoLabels,
  selectInboxInfoLoading,
  selectSelectedContactID,
  selectLabelPermissions,
} from "../../redux/selectors";
import {
  fetchLabels,
  assignLabel,
  createAndAssignLabel,
  removeLabelFromContact,
  addNote,
  updateNote,
  deleteNote,
  setContactInfo,
  invalidateCachedContact,
  updateGroupDescription,
  updateGroupSubject,
} from "../../redux/Slice/Inbox/inboxInfoSlice";
import { updateContact } from "../../redux/Slice/Contacts/contactsSlice";
import { fetchContactProfileFields } from "../../redux/Slice/Settings/contactProfileFieldsSlice";
import { setSelectedOption } from "../../redux/Slice/Settings/settingsSlice";
import { updateContactName } from "../../redux/Slice/Inbox/InboxMessageSlice";
import { updateContactNameInInbox } from "../../redux/Slice/Inbox/inboxRecentSlice";
import {
  fetchScheduledMessages,
  cancelScheduledMessage,
} from "../../redux/Slice/Inbox/ScheduledMessageSlice";
import { isSubscriptionBlockedError } from "../../utils/subscriptionError";
import { useSubscriptionGuard } from "../../hooks/useSubscriptionGuard";
import { slimScrollbarSx } from "../../utils/scrollbarStyles";
import { nameToContactAvatarImage, NON_DRAGGABLE_AVATAR_IMG_PROPS } from "../../config/AvatarColor";
import PlatformIcon from "./Features/PlatformIcon";
import { ShopifyOrdersPanel } from "./ChatInfoShopifyOrders";
import { invalidate, STORES } from "../../utils/indexedDBCache";

const MAX_LABELS_PER_CONTACT = 5;
const MAX_NOTES_PER_CONTACT = 10;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// <input type> for each custom Contact Profile field type.
const CUSTOM_FIELD_INPUT_TYPES = { text: "text", number: "number", email: "email", phoneNumber: "tel" };
const customFieldKey = (fieldId) => `custom:${fieldId}`;

// Module-level (not component-level) — survives ContactDetailsSection
// unmounting/remounting every time the Profile icon is closed/reopened.
// fetchLabels() is cache-first (IndexedDB), so re-dispatching it on every
// remount could serve a STALE cached list and clobber the freshly-updated
// in-memory list right after creating a label — found 2026-08-22 (Rahim:
// "when select existing label it doesnt show the ones that have been
// added"). Matches ChatInfo.jsx's own once-per-session labelsFetchedRef
// behavior, which never had this bug because that component stays mounted
// for the whole session instead of remounting per icon-click.
let labelsFetchedThisSession = false;

// Slim icon rail — replaces ChatInfo's always-open XL inline panel (Rahim,
// 2026-08-21: "detach the current detail panel and introduce a new column
// with new icons"). ChatInfo.jsx's own component (still mounted invisibly
// for its socket listener, see the 2026-08-21 architecture note) is
// untouched — this file reuses the SAME redux thunks/selectors ChatInfo.jsx
// does, just triggered by this rail's own click-gating instead of
// mount/accordion-gating.
//
// Sits under the real ChatboxNavbar (rendered above it by Inbox.jsx,
// spanning this column too) rather than faking the navbar's height/color
// with a second box of its own (Rahim, 2026-08-21: "remove the box... use
// the top navbar to stretch the end of the page").
//
// Only 2 sections (Rahim, 2026-08-21) — Contact details (respond.io-style:
// pfp header + editable core fields + Labels/Notes as closed accordions,
// Rahim 2026-08-22) and Scheduled Messages (its own drawer).
const RAIL_ITEMS = [
  { key: "profile", Icon: ProfileCircle, label: "Contact details", title: "Contact Details" },
  { key: "scheduled", Icon: Clock, label: "Scheduled messages", title: "Scheduled Messages" },
];

// Group counterpart of RAIL_ITEMS (WhatsApp Groups plan follow-up, 2026-09-26): "Contact
// details" makes no sense for a group -- contactInfo is always null for one (selectGroup
// clears it and never repopulates, per this feature's own earlier build), so that section
// used to just render "No contact selected." for a group, a dead end. Swapped for "Group
// description" instead -- DocumentText (already imported, no new icon needed) reads as
// "descriptive text," distinct from ProfileCircle's implied "a person." "Scheduled messages"
// is left in for groups too -- nothing about it is 1:1-only (no scheduling UI targets a group
// yet, so it just, correctly, shows "No scheduled messages"), and the user didn't ask for it
// to be hidden.
// Label/title updated to "Group info" (2026-09-26 follow-up: group name editing added to this
// same section's header, alongside description) -- the `key` stays "groupDescription" so no
// other reference to it (activeSection state, etc.) needs touching.
const GROUP_RAIL_ITEMS = [
  { key: "groupDescription", Icon: DocumentText, label: "Group info", title: "Group Info" },
  { key: "scheduled", Icon: Clock, label: "Scheduled messages", title: "Scheduled Messages" },
];

// Shopify orders — its own rail icon, under the clock, shown ONLY for a contact that is a Shopify
// customer (contactInfo.shopifyCustomerId — see railItems in ChatInfoRail below). It used to live
// only in the old ChatInfo.jsx panel and was lost when the slim rail replaced that panel on XL/LG
// screens; it gets its own panel here rather than being buried inside Contact Details.
const SHOPIFY_RAIL_ITEM = { key: "shopify", Icon: ShoppingBag, label: "Shopify orders", title: "Shopify Orders" };

const RAIL_WIDTH = 64;
const DRAWER_DEFAULT_WIDTH = 340;
// Same min/max SHAPE as InboxAll's own resizable panel (260-560), just
// narrower on the low end — this panel's content (contact fields, a
// scheduled-messages list) doesn't need as much room as a full conversation
// list to stay usable (Rahim, 2026-08-21: "make the drawer resizable...
// jsut like we did for te contact menu").
const DRAWER_MIN_WIDTH = 280;
// Breakpoint-aware ceiling, not one flat constant (2026-08-27, small-laptop
// responsiveness pass, same reasoning as InboxAll's own split in Inbox.jsx)
// — 560 was tuned for XL's 1536px floor; now that this rail also renders at
// LG (1200px floor), it needs a tighter ceiling there so InboxAll + this
// drawer can't together starve the chat body of width.
// Lowered again 2026-09-10 (560->480, 420->380, mirrored in Inbox.jsx's own
// INBOX_ALL_MAX_WIDTH_*) -- each panel having its own independent cap didn't
// stop a user from maxing out BOTH InboxAll and this drawer at once, which
// still left the chat body squeezed down near/below its own 360px floor
// (Rahim, 2026-09-10, screenshot: chat column reduced to a sliver with both
// drawers wide). See Inbox.jsx's own comment for the exact math.
const DRAWER_MAX_WIDTH_XL = 480;
const DRAWER_MAX_WIDTH_LG = 380;
const DRAWER_WIDTH_STORAGE_KEY = "chatInfoDrawerWidthPx";

// Small square wrapper for an Iconsax icon at a fixed px size — Iconsax icons
// don't support MUI's `sx` prop (see icon-library-iconsax memory), so any
// non-default size needs this Box wrapper instead.
const IconBox = ({ size = 16, children }) => (
  <Box sx={{ width: size, height: size, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
    {children}
  </Box>
);

// Custom dropdown-arrow for MUI Select — replaces the default ExpandMoreIcon
// with the app's own Iconsax chevron (Rahim, 2026-08-22: "i dont like the
// drop down icon of the select existing label container"). Forwards
// `className` so MUI's own `.MuiSelect-icon` positioning/open-state rotation
// still applies — only the glyph itself changes.
const SelectDropdownIcon = ({ className }) => (
  <Box component="span" className={className} sx={{ width: 16, height: 16, display: "flex", alignItems: "center", justifyContent: "center" }}>
    <ArrowDown2 size="100%" color="currentColor" variant="Linear" />
  </Box>
);

// Shared row wrapper — same visual shape ChatInfo.jsx used for every
// label/value STACKED vertically — label on top, value on the line below
// (Rahim, 2026-08-22: "dont make the texts side by side... do it like
// respond.io do it up and down"), each row with breathing room below it.
function InfoRow({ label, children }) {
  return (
    <Box>
      <Typography color="textSecondary" sx={{ fontSize: "0.8125rem", mb: 0.5 }}>
        {label}
      </Typography>
      <Box display="flex" alignItems="center" gap={0.5} sx={{ minWidth: 0 }}>
        {children}
      </Box>
    </Box>
  );
}

// Always-visible section shell — no dropdown/chevron, content just sits
// under the title permanently (Rahim, 2026-08-22: "there is no need of the
// drop downs for the other section like allways assigned to etc keep it
// shown"). Same title style + padding the old accordion used, just without
// the click-to-expand chrome.
function DrawerSection({ title, headerAction, contentGap = 0, tint, children }) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        pl: "18px",
        pr: "12px",
        pt: 1.5,
        // A negative contentGap signals "nothing follows the heading" —
        // tighten the bottom padding to match instead of leaving the same
        // generous pb as a section that actually has content.
        pb: contentGap < 0 ? 1 : 2.25,
        // Optional low-opacity color wash for the whole section — e.g. a
        // faint yellow behind Notes for a sticky-note feel (Rahim,
        // 2026-08-22: "add yellow colour with opacty very low as the
        // background of notes section giving a feel of notes"). Mode-aware
        // alpha so it reads the same subtle weight in both themes.
        bgcolor: tint ? (theme.palette.mode === "dark" ? `${tint}14` : `${tint}0D`) : "transparent",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 + contentGap }}>
        <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600, color: theme.palette.text.primary }}>
          {title}
        </Typography>
        {headerAction}
      </Box>
      {children}
    </Box>
  );
}

// Mirrors ContactDetailsSection's real structure section-by-section (header
// card, Profile fields, Sticky Agent, Labels, Notes) — same reasoning as the
// ChatBody/ChatboxNavbar skeletons built earlier this session: a placeholder
// that already has the right shape reads as "this is loading", a generic
// centered spinner reads as "something might be broken" (Rahim, 2026-08-29:
// "go ahead for both schedule and the contact"). Only reachable if
// contactInfo's own fetch (ChatInfo.jsx's invisible mount) lags behind
// messages — rare, but real, since the two are separate API calls.
function ContactDetailsSkeleton() {
  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, pl: "18px", pr: "16px", py: 2.25 }}>
        <Skeleton variant="circular" width={54} height={54} sx={{ flexShrink: 0 }} />
        <Box sx={{ minWidth: 0, flex: 1, display: "flex", flexDirection: "column", gap: 0.75 }}>
          <Skeleton variant="text" width="60%" height={20} />
          <Skeleton variant="text" width="40%" height={14} />
          <Box sx={{ display: "flex", gap: 0.5, mt: 0.5 }}>
            <Skeleton variant="rounded" width={54} height={18} sx={{ borderRadius: "8px" }} />
            <Skeleton variant="rounded" width={54} height={18} sx={{ borderRadius: "8px" }} />
          </Box>
        </Box>
      </Box>
      <Divider sx={{ my: 1 }} />
      <Box sx={{ pl: "18px", pr: "12px", py: 1.5, display: "flex", flexDirection: "column", gap: 1.75 }}>
        {[70, 90, 60, 55].map((labelW, i) => (
          <Box key={i}>
            <Skeleton variant="text" width={labelW} height={14} sx={{ mb: 0.5 }} />
            <Skeleton variant="text" width="80%" height={18} />
          </Box>
        ))}
      </Box>
      <Divider sx={{ my: 1 }} />
      <Box sx={{ pl: "18px", pr: "12px", pt: 1.5, pb: 2.25 }}>
        <Skeleton variant="text" width={90} height={18} sx={{ mb: 1.75 }} />
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5 }}>
          <Skeleton variant="text" width="65%" height={16} />
          <Skeleton variant="rounded" width={34} height={18} sx={{ borderRadius: "10px", flexShrink: 0 }} />
        </Box>
      </Box>
      <Divider />
      <Box sx={{ pl: "18px", pr: "12px", pt: 1.5, pb: 2.25 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.75 }}>
          <Skeleton variant="text" width={60} height={18} />
          <Skeleton variant="circular" width={20} height={20} />
        </Box>
        <Box sx={{ display: "flex", gap: 0.75 }}>
          <Skeleton variant="rounded" width={64} height={24} sx={{ borderRadius: "16px" }} />
          <Skeleton variant="rounded" width={48} height={24} sx={{ borderRadius: "16px" }} />
        </Box>
      </Box>
      <Divider />
      <Box sx={{ pl: "18px", pr: "12px", pt: 1.5, pb: 2.25 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.75 }}>
          <Skeleton variant="text" width={55} height={18} />
          <Skeleton variant="circular" width={20} height={20} />
        </Box>
        {[0, 1].map((i) => (
          <Box key={i} sx={{ display: "flex", gap: 1.5, mb: 1.75, alignItems: "flex-start" }}>
            <Skeleton variant="circular" width={6} height={6} sx={{ mt: 0.75, flexShrink: 0 }} />
            <Skeleton variant="text" width="90%" height={16} />
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// Mirrors a pending scheduled-message card's real shape (type badge, preview
// line, time row, cancel icon) — same rail-loading-skeleton pass as above.
// Sized to sit inside ScheduledMessagesSection's own p:1.5 wrapper, matching
// the loaded state's own unpadded column exactly (no extra padding here).
function ScheduledMessagesSkeleton() {
  const theme = useTheme();
  return (
    <Box display="flex" flexDirection="column" gap={1.75}>
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          sx={{ p: 1.75, border: `1px solid ${theme.palette.divider}`, borderRadius: 2, bgcolor: theme.palette.background.default }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1.5}>
            <Box flex={1} sx={{ minWidth: 0 }}>
              <Skeleton variant="rounded" width={70} height={20} sx={{ borderRadius: "6px", mb: 1 }} />
              <Skeleton variant="text" width="85%" height={16} sx={{ mb: 1 }} />
              <Skeleton variant="text" width="50%" height={14} />
            </Box>
            <Skeleton variant="circular" width={24} height={24} sx={{ flexShrink: 0 }} />
          </Box>
        </Box>
      ))}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Contact Details section — mounted only while activeSection === "profile",
// so every fetch it dispatches on open is naturally click-gated. contactInfo
// itself needs no fetch here at all — it's already resident in Redux by the
// time this can be opened (ChatInfo.jsx's invisible mount fetches it
// unconditionally on contact selection). The one real fetch is fetchLabels()
// (workspace label list, for the add-label picker) — gated further down to
// the Labels accordion actually being opened, not just this section opening.
function ContactDetailsSection({ contactId }) {
  const theme = useTheme();
  // Dark mode: white for this drawer's neutral utility icons instead of
  // grey (Rahim, 2026-08-22: "use white for all icons"). Light mode
  // unchanged.
  const iconColor = theme.palette.mode === "dark" ? "#FFFFFF" : theme.palette.text.secondary;
  const dispatch = useDispatch();
  const { guard } = useSubscriptionGuard();

  const contactInfo = useSelector(selectContactInfo);
  // Only an official WhatsApp contact can have a WhatsApp Web link (same eligibility the server applies). Not gated on isQrChat:
  // a Meta contact in a workspace with a linked QR number can carry a connectionId.
  const canHaveWhatsappWebLink = Boolean(contactInfo?._id) && (!contactInfo?.platform || contactInfo.platform === "whatsapp");
  const { status: whatsappWebInfo, reset: resetWhatsappWeb, resetting: resettingWhatsappWeb, revoke: revokeWhatsappWeb, revoking: revokingWhatsappWeb } = useWhatsappWebStatus(contactInfo?._id, canHaveWhatsappWebLink);
  // Meta's 24h reply-window rule doesn't apply to a QR-connected workspace (same
  // isQrConnectedWorkspace as TextBox.jsx/MessageCard.jsx) -- gates the Active/Inactive
  // status label and hides the "Xh left" pill below, both otherwise driven by
  // contactInfo.hoursRemaining, a Meta-only concept.
  const isQrOnlyWorkspace = useSelector((s) => s.workspace?.currentWorkspace?.whatsappConnectionType === "baileys");
  const isQrConnectedWorkspace = isQrOnlyWorkspace || isQrChat(contactInfo);
  const isLoading = useSelector(selectInboxInfoLoading);
  const workspaceLabels = useSelector(selectInboxInfoLabels);
  const LabelPermissions = useSelector(selectLabelPermissions);
  const ContactPermissions = useSelector(
    (state) => state.userDetails.userPermissions?.permissions?.Contact || {}
  );
  const NotesPermissions = useSelector(
    (state) => state.userDetails.userPermissions?.permissions?.Notes || {}
  );
  const canViewPII = !!ContactPermissions?.ViewPersonalIdentificationInfo;
  const hasLabelViewPermission = !!LabelPermissions?.View;

  // Custom Contact Profile fields (Settings -> Contact Profile). Only the ones marked
  // "shown in Inbox" appear here. The "+" next to Profile jumps to that settings tab and
  // is only offered to members allowed to add fields (Owner always is, see roleMiddleware.js).
  const navigate = useNavigate();
  const { accountid } = useParams();
  const workspaceId = useSelector(
    (state) => state.auth?.user?.workspaceId || state.workspace?.currentWorkspace?._id
  );
  const profileFields = useSelector((state) => state.contactProfileFields.items).filter((f) => f.showInInbox);
  const canAddProfileFields = useSelector(
    (state) =>
      state.userDetails.userDetails?.roleName === "Owner" ||
      state.userDetails.userPermissions?.permissions?.ContactProfileFields?.Add === true
  );
  React.useEffect(() => {
    if (workspaceId) dispatch(fetchContactProfileFields(workspaceId));
  }, [dispatch, workspaceId]);
  const openContactProfileSettings = () => {
    dispatch(setSelectedOption("Contact Profile"));
    navigate(`/home/${accountid}/settings`);
  };

  const [snackbar, setSnackbar] = React.useState({ open: false, message: "", severity: "success" });
  const notify = (message, severity = "success") => setSnackbar({ open: true, message, severity });

  // --- Core fields (Name / Business name / Email). Two-level toggle (Rahim,
  // 2026-08-22): the ONE Edit/Save button beside "Profile" arms edit mode —
  // it doesn't turn every field into an input by itself. Once armed, each
  // editable row shows its OWN small pencil (like the old panel's per-field
  // icon); clicking a row's pencil is what actually turns THAT field into an
  // input. Save (same top button, now showing a check) commits whatever
  // fields were actually changed in one PUT and exits edit mode entirely.
  // Phone is not editable for WhatsApp contacts — it's the real WhatsApp number, matches the old
  // panel's behavior. A website visitor's phone is just what they typed into the pre-chat form
  // (or nothing), so it's editable for them — and only for viewers allowed to see it unmasked.
  const [editModeOn, setEditModeOn] = React.useState(false);
  const [activeFields, setActiveFields] = React.useState({ name: false, businessName: false, email: false, phone: false });
  const [fieldValues, setFieldValues] = React.useState({ name: "", businessName: "", email: "", phone: "" });
  const [savingFields, setSavingFields] = React.useState(false);
  const isPhoneEditable = contactInfo?.platform === "website" && canViewPII;
  const displayPhone = contactInfo?.phoneNumberID ? `+${contactInfo.phoneNumberID}` : "";

  const startEditMode = () => {
    setFieldValues({
      name: contactInfo?.name || "",
      businessName: contactInfo?.businessName || "",
      email: contactInfo?.email || "",
      phone: displayPhone,
      ...Object.fromEntries(
        profileFields.map((f) => [customFieldKey(f._id), contactInfo?.customFields?.[f._id] || ""])
      ),
    });
    setActiveFields({ name: false, businessName: false, email: false, phone: false });
    setEditModeOn(true);
  };
  const toggleFieldActive = (field) => setActiveFields((a) => ({ ...a, [field]: !a[field] }));

  const handleSaveFields = async () => {
    if (!contactInfo?._id || savingFields) return;
    const trimmed = {
      name: fieldValues.name.trim(),
      businessName: fieldValues.businessName.trim(),
      email: fieldValues.email.trim(),
      phone: fieldValues.phone.trim(),
    };
    if (trimmed.email && !EMAIL_REGEX.test(trimmed.email)) {
      notify("Please enter a valid email address", "warning");
      return;
    }
    // Only send what actually changed — one PUT covers all changed fields
    // instead of the old panel's one-call-per-field pattern.
    const updateData = {};
    const changedCustomFields = {};
    for (const field of profileFields) {
      const value = (fieldValues[customFieldKey(field._id)] ?? "").trim();
      if (value === (contactInfo.customFields?.[field._id] || "")) continue;
      if (value && field.type === "email" && !EMAIL_REGEX.test(value)) {
        notify(`Please enter a valid email for ${field.name}`, "warning");
        return;
      }
      if (value && field.type === "number" && !Number.isFinite(Number(value))) {
        notify(`${field.name} must be a number`, "warning");
        return;
      }
      changedCustomFields[field._id] = value;
    }
    if (Object.keys(changedCustomFields).length > 0) updateData.customFields = changedCustomFields;
    if (trimmed.name !== (contactInfo.name || "")) updateData.name = trimmed.name;
    if (trimmed.businessName !== (contactInfo.businessName || "")) updateData.businessName = trimmed.businessName;
    if (trimmed.email !== (contactInfo.email || "")) updateData.email = trimmed.email;
    // Server validates/normalizes it; an empty value is ignored rather than clearing the phone.
    if (isPhoneEditable && trimmed.phone && trimmed.phone !== displayPhone) updateData.phoneNumberID = trimmed.phone;
    if (Object.keys(updateData).length === 0) {
      setEditModeOn(false);
      return;
    }
    if (!(await guard([]))) return;
    setSavingFields(true);
    try {
      const updatedContact = await dispatch(updateContact({ id: contactInfo._id, updateData })).unwrap();
      // Apply the server's authoritative response directly instead of re-fetching
      // (a re-fetch would just return the stale in-memory/IndexedDB contact cache).
      invalidateCachedContact(contactInfo._id);
      dispatch(setContactInfo(updatedContact));
      if (updateData.name !== undefined) {
        dispatch(updateContactName({ contactId: contactInfo._id, name: updateData.name }));
        dispatch(updateContactNameInInbox({ contactId: contactInfo._id, name: updateData.name }));
      }
      notify("Contact details updated");
      setEditModeOn(false);
    } catch (err) {
      // The thunk rejects with the server's message as a plain string
      notify((typeof err === "string" ? err : err?.message) || "Failed to update contact", "error");
    } finally {
      setSavingFields(false);
    }
  };

  const renderField = (label, field, displayValue, inputType = "text") => (
    <InfoRow label={label}>
      {editModeOn && activeFields[field] ? (
        <TextField
          size="small"
          fullWidth
          autoFocus
          type={inputType}
          value={fieldValues[field] ?? ""}
          onChange={(e) => setFieldValues((v) => ({ ...v, [field]: e.target.value }))}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); handleSaveFields(); }
            else if (e.key === "Escape") toggleFieldActive(field);
          }}
          sx={{ "& .MuiInputBase-input": { paddingY: 0.5, fontSize: "0.875rem" } }}
        />
      ) : (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", gap: 1 }}>
          <Typography
            color="textPrimary"
            title={displayValue || "-"}
            sx={{ fontSize: "0.875rem", fontWeight: 500, minWidth: 0, whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}
          >
            {displayValue || "-"}
          </Typography>
          {editModeOn && (
            <IconButtonWithTooltip label="Edit" size="small" onClick={() => toggleFieldActive(field)} sx={{ p: 0.5, flexShrink: 0 }}>
              <IconBox size={13}><Edit2 size="100%" color="currentColor" variant="Linear" /></IconBox>
            </IconButtonWithTooltip>
          )}
        </Box>
      )}
    </InfoRow>
  );

  // --- Notes ---
  const contactNotes = Array.isArray(contactInfo?.notes) ? contactInfo.notes : [];
  const [newNoteText, setNewNoteText] = React.useState("");
  const noteInputRef = React.useRef(null);
  // Hidden until the heading's plus is clicked, with its own close button —
  // was always-visible with no way to hide it again (Rahim, 2026-08-22:
  // "its still showing the note section even tho i havent clicked the
  // button... even if i did there is no cross to close it"). Same pattern
  // as Labels' isAddingLabel.
  const [isAddingNote, setIsAddingNote] = React.useState(false);
  const [editingNoteId, setEditingNoteId] = React.useState(null);
  const [editingNoteText, setEditingNoteText] = React.useState("");
  const [noteBusy, setNoteBusy] = React.useState(false);

  const handleAddNote = async () => {
    if (!newNoteText.trim() || !NotesPermissions.Add || !contactInfo?._id) return;
    if (contactNotes.length >= MAX_NOTES_PER_CONTACT) {
      notify(`You can add up to ${MAX_NOTES_PER_CONTACT} notes per contact`, "warning");
      return;
    }
    if (!(await guard([]))) return;
    setNoteBusy(true);
    try {
      await dispatch(addNote({ contactId: contactInfo._id, text: newNoteText })).unwrap();
      setNewNoteText("");
      setIsAddingNote(false);
      notify("Note added");
    } catch (err) {
      if (!isSubscriptionBlockedError(err)) notify(err || "Failed to add note", "error");
    } finally {
      setNoteBusy(false);
    }
  };
  const handleSaveNote = async (noteId) => {
    if (!editingNoteText.trim() || !NotesPermissions.Edit || !contactInfo?._id) return;
    if (!(await guard([]))) return;
    setNoteBusy(true);
    try {
      await dispatch(updateNote({ contactId: contactInfo._id, noteId, text: editingNoteText })).unwrap();
      setEditingNoteId(null);
      setEditingNoteText("");
      notify("Note updated");
    } catch (err) {
      if (!isSubscriptionBlockedError(err)) notify(err || "Failed to update note", "error");
    } finally {
      setNoteBusy(false);
    }
  };
  const handleDeleteNote = async (noteId) => {
    if (!NotesPermissions.Delete || !contactInfo?._id) return;
    if (!(await guard([]))) return;
    setNoteBusy(true);
    try {
      await dispatch(deleteNote({ contactId: contactInfo._id, noteId })).unwrap();
      notify("Note deleted");
    } catch (err) {
      if (!isSubscriptionBlockedError(err)) notify(err || "Failed to delete note", "error");
    } finally {
      setNoteBusy(false);
    }
  };

  // --- Always Assigned ---
  const [savingAlwaysAssigned, setSavingAlwaysAssigned] = React.useState(false);
  const isAssigned = contactInfo?.status === "Assigned" && !!contactInfo?.assignedTo;
  const handleToggleAlwaysAssigned = async (checked) => {
    if (!contactInfo?._id) return;
    if (checked && !isAssigned) {
      notify("Contact must be assigned first", "warning");
      return;
    }
    if (!(await guard([]))) return;
    setSavingAlwaysAssigned(true);
    try {
      const updatedContact = await dispatch(
        updateContact({
          id: contactInfo._id,
          updateData: { alwaysAssignedEnabled: checked, alwaysAssignedTo: checked ? null : null },
        })
      ).unwrap();
      invalidateCachedContact(contactInfo._id);
      dispatch(setContactInfo(updatedContact));
      notify(checked ? "Sticky agent enabled" : "Sticky agent disabled");
    } catch (err) {
      notify(err?.message || "Failed to update", "error");
    } finally {
      setSavingAlwaysAssigned(false);
    }
  };

  // --- Labels ---
  const contactLabels = Array.isArray(contactInfo?.labels) ? contactInfo.labels : [];
  const workspaceLabelsList = Array.isArray(workspaceLabels) ? workspaceLabels : [];
  const hasReachedLabelsLimit = contactLabels.length >= MAX_LABELS_PER_CONTACT;
  const [isAddingLabel, setIsAddingLabel] = React.useState(false);
  const [newLabelText, setNewLabelText] = React.useState("");
  const [selectedLabel, setSelectedLabel] = React.useState("");
  const [labelBusy, setLabelBusy] = React.useState(false);
  const [removingLabel, setRemovingLabel] = React.useState("");
  // Labels is always visible now, no accordion to gate on (Rahim, 2026-08-22:
  // "no need of the drop downs for the other section... keep it shown") —
  // fetched once per whole session (module-level flag above), not once per
  // Profile-section open, to avoid a stale cache-first refetch clobbering a
  // just-created label.
  React.useEffect(() => {
    if (hasLabelViewPermission && !labelsFetchedThisSession) {
      labelsFetchedThisSession = true;
      dispatch(fetchLabels());
    }
  }, [dispatch, hasLabelViewPermission]);

  // Force a real network refetch (not the cache-first fetchLabels() path) the
  // moment the picker opens — neither createAndAssignLabel nor
  // removeLabelFromContact invalidates the IndexedDB label cache, only the
  // in-memory Redux one, so without this the dropdown can show labels that
  // were deleted elsewhere (another tab, the workspace Labels page) for the
  // rest of the session (Rahim, 2026-08-22: "showing past deleted ones...
  // check proeplry have you broken the functioanlity or not fetching data
  // corectly").
  const handleOpenAddLabel = async () => {
    setIsAddingLabel(true);
    await invalidate(STORES.LABELS, "all");
    dispatch(fetchLabels());
  };

  const handleSaveLabel = async () => {
    if (!contactInfo?._id || (!newLabelText && !selectedLabel)) return;
    if (hasReachedLabelsLimit) {
      notify(`Maximum ${MAX_LABELS_PER_CONTACT} labels allowed per contact`, "warning");
      return;
    }
    if (!(await guard([]))) return;
    setLabelBusy(true);
    try {
      if (newLabelText) {
        await dispatch(createAndAssignLabel({ labelName: newLabelText, contactId: contactInfo._id })).unwrap();
      } else {
        await dispatch(assignLabel({ labelName: selectedLabel, contactId: contactInfo._id })).unwrap();
      }
      notify("Label assigned");
      setIsAddingLabel(false);
      setNewLabelText("");
      setSelectedLabel("");
    } catch (err) {
      notify(err?.message || "Failed to assign label", "error");
    } finally {
      setLabelBusy(false);
    }
  };
  const handleRemoveLabel = async (labelName) => {
    if (!contactInfo?._id) return;
    if (!(await guard([]))) return;
    setRemovingLabel(labelName);
    setLabelBusy(true);
    try {
      await dispatch(removeLabelFromContact({ labelName, contactId: contactInfo._id })).unwrap();
      notify(`Label '${labelName}' removed`);
    } catch (err) {
      notify(err?.error || "Failed to remove label", "error");
    } finally {
      setRemovingLabel("");
      setLabelBusy(false);
    }
  };

  if (isLoading && !contactInfo) {
    return <ContactDetailsSkeleton />;
  }
  if (!contactInfo) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography sx={{ fontSize: "0.8125rem", color: theme.palette.text.secondary, textAlign: "center" }}>
          No contact selected.
        </Typography>
      </Box>
    );
  }

  const showPhone = contactInfo.platform !== "instagram" && contactInfo.platform !== "facebook";
  // Website chat and QR-connected numbers have no 24-hour window, so they're never "Inactive" and has no "Xh left" pill. Judged per chat: an
  // Instagram or Facebook chat of a QR-only workspace keeps its window (see hasMetaServiceWindow).
  const hasServiceWindow = hasMetaServiceWindow({ platform: contactInfo.platform, isQrConnectedWorkspace });
  const statusLabel = hasServiceWindow && contactInfo.hoursRemaining === 0 ? "Inactive" : "Active";
  // Plain text under the name now, not a pill (Rahim, 2026-08-22: "use
  // Assined to: as seperate with light grey colour and the name of the team
  // or team mmeber no need to write it in pill") — only rendered when the
  // contact is actually assigned to someone/some team, same as ChatboxNavbar
  // only shows its own assigned-status row in that case.
  const assigneeName =
    contactInfo.status === "Assigned"
      ? contactInfo.assignedToName || (contactInfo.assignedToTeam ? `Team ${contactInfo.assignedToTeam}` : contactInfo.assignedTo) || null
      : null;

  return (
    <Box>
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} sx={{ width: "100%" }} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Header card — pfp stays its own left column (own fixed position,
          not centered/re-flowed by whatever's beside it), a 3-row text stack
          sits to its right: Name / "Assigned to" / pills. Matches the
          reference formation exactly (avatar left, spanning the stack;
          Name-then-secondary-line-then-icon-row to its right) — not the data,
          just that shape (Rahim, 2026-08-22: "keep pfp left alligned then
          name under that assigned and under the pfp both pills... just the
          formation dont copy the data"). Avatar is the same deterministic
          mock-avatar-per-name image already used everywhere else contacts
          are shown (ChatboxNavbar/MessageCard) — real contacts never have an
          actual uploaded photo on file, so this isn't new/invented data,
          just the existing convention reused. */}
      {/* alignItems:"center" (not "flex-start") — the outer py IS symmetric,
          but the text column (Name/Assigned/pills) is now taller than the
          48px avatar, so top-anchoring the avatar left visibly more empty
          space under it than above it. Centering it in the row fixes that
          without touching the text column's own top-alignment (it's the
          taller item, so centering doesn't shift it) (Rahim, 2026-08-22: "i
          dont thin the padding from top and bottom are same"). */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, pl: "18px", pr: "16px", py: 2.25 }}>
        <Box sx={{ position: "relative", display: "inline-flex", flexShrink: 0 }}>
          <Avatar
            src={nameToContactAvatarImage(contactInfo.name)}
            imgProps={NON_DRAGGABLE_AVATAR_IMG_PROPS}
            sx={{
              width: 54,
              height: 54,
              bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.05)",
              border: `1.5px solid ${theme.palette.divider}`,
            }}
          />
          <Box
            sx={{
              position: "absolute",
              bottom: -2,
              right: -2,
              borderRadius: "50%",
              border: `2px solid ${theme.palette.background.default}`,
              display: "flex",
            }}
          >
            <PlatformIcon platform={contactInfo.platform || "whatsapp"} size={18} sx={{ ml: 0 }} />
          </Box>
        </Box>

        <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column" }}>
          {/* Name + "Assigned to" grouped tightly (they're both identity
              info about the contact) — the pills row gets its own mt below
              to read as a separate group, instead of one flat gap treating
              all 3 rows the same (Rahim, 2026-08-22: "use better spacing
              and everything"). */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
            {/* Row 1 — Name */}
            <Typography
              title={contactInfo.name || "N/A"}
              sx={{
                fontSize: "0.9375rem",
                fontWeight: 600,
                color: theme.palette.text.primary,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {contactInfo.name || "N/A"}
            </Typography>

            {/* Row 2 — Assigned to. Value needs its own minWidth:0 — without
                it, a flex item's default min-width is its own content width,
                so it never actually shrinks enough to trigger ellipsis, it
                just overflows the row instead (found during the 2026-08-22
                drawer-wide truncation audit, Rahim: "check the ...
                functioanlity for the whole drawer"). */}
            {assigneeName && (
              <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5, minWidth: 0 }}>
                <Typography sx={{ fontSize: "0.75rem", color: theme.palette.text.secondary, flexShrink: 0 }}>
                  Assigned to:
                </Typography>
                <Typography
                  title={assigneeName}
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    color: theme.palette.text.primary,
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {assigneeName}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Row 3 — Status / Expires-in pills. Same box model as
              LifecycleStageChip's "solid" variant (ChatboxNavbar's stage
              chip / Close button) — square-ish, uncolored, bordered. */}
          <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 0.5, mt: 0.75 }}>
            <Box
              sx={{
                borderRadius: "8px",
                bgcolor: theme.palette.background.default,
                border: `1px solid ${theme.palette.divider}`,
                px: "6px",
                py: "2px",
              }}
            >
              <Typography sx={{ fontSize: "0.6875rem", fontWeight: 500, color: theme.palette.text.primary, whiteSpace: "nowrap" }}>
                {statusLabel}
              </Typography>
            </Box>
            {hasServiceWindow && (
              <Box
                sx={{
                  borderRadius: "8px",
                  bgcolor: theme.palette.background.default,
                  border: `1px solid ${theme.palette.divider}`,
                  px: "6px",
                  py: "2px",
                }}
              >
                <Typography sx={{ fontSize: "0.6875rem", fontWeight: 500, color: theme.palette.text.secondary, whiteSpace: "nowrap" }}>
                  {contactInfo.hoursRemaining || 0}h left
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <Divider sx={{ my: 1 }} />

      {/* Core fields — one Edit toggle for the whole block (Rahim, 2026-08-22:
          "instead of manage we wil give button of edit with a pen icon...
          he can edit only when clicked on the edit button"). Rows sit in a
          gapped flex column (not stacked padding) so the spacing between
          them is consistent and easy to tune in one place (Rahim, 2026-08-22:
          "add better spacing"). Section label is "Profile", not "Contact
          Details" — the drawer's own top header already says that, so this
          inner heading needed different wording, not no heading (Rahim,
          2026-08-22: "u removed the heading entirely use something other
          than contact detail"). pl matches InboxSettings.jsx's established
          18px left inset so this drawer's content lines up with every other
          Inbox menu (Rahim, 2026-08-22: "all have proper equal left
          padding"). */}
      <Box sx={{ pl: "18px", pr: "12px", py: 1.5 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.75 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600, color: theme.palette.text.primary }}>
              Profile
            </Typography>
            {canAddProfileFields && (
              <IconButtonWithTooltip label="Add profile field" size="small" onClick={openContactProfileSettings} sx={{ p: 0.5 }}>
                <IconBox size={15}><Add size="100%" color="currentColor" variant="Linear" /></IconBox>
              </IconButtonWithTooltip>
            )}
          </Box>
          <Box
            onClick={savingFields ? undefined : editModeOn ? handleSaveFields : startEditMode}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              cursor: savingFields ? "default" : "pointer",
              color: theme.palette.text.secondary,
              "&:hover": { color: theme.palette.text.primary },
            }}
          >
            {savingFields ? (
              <CircularProgress size={13} />
            ) : (
              <IconBox size={14}>
                {editModeOn
                  ? <TickCircle size="100%" color="currentColor" variant="Bold" />
                  : <Edit2 size="100%" color="currentColor" variant="Linear" />}
              </IconBox>
            )}
            <Typography sx={{ fontSize: "0.8125rem", fontWeight: 500, color: "inherit" }}>
              {editModeOn ? "Save" : "Edit"}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.75 }}>
          {renderField("Name", "name", contactInfo.name)}
          {renderField("Business name", "businessName", contactInfo.businessName)}
          {renderField("Email", "email", contactInfo.email)}
          {showPhone && isPhoneEditable && renderField("Phone", "phone", displayPhone)}
          {showPhone && !isPhoneEditable && (
            <InfoRow label="Phone">
              <Typography color="textPrimary" sx={{ fontSize: "0.875rem", fontWeight: 500 }}>
                {canViewPII && contactInfo.phoneNumberID ? `+${contactInfo.phoneNumberID}` : "***"}
              </Typography>
            </InfoRow>
          )}
          {profileFields.map((field) => (
            <React.Fragment key={field._id}>
              {renderField(
                field.name,
                customFieldKey(field._id),
                contactInfo.customFields?.[field._id],
                CUSTOM_FIELD_INPUT_TYPES[field.type]
              )}
            </React.Fragment>
          ))}
        </Box>
      </Box>

      <Divider sx={{ my: 1 }} />

      {/* "Other fields" — closed by default (Rahim, 2026-08-22), same
          dropdown/accordion pattern the old panel used. The old "Conversation
          Details" accordion (Status/Expires in/Assigned/Lifecycle) is gone —
          Status/Assigned/Expires-in all moved up to header pills, Lifecycle
          was dropped entirely per Rahim's explicit call, leaving nothing left
          to show there. */}

      {/* Label-only rename to "Sticky Agent" (Rahim, 2026-08-27) — same
          alwaysAssignedEnabled field/toggle behavior underneath, just
          renamed in the UI copy; not a functional change. */}
      <DrawerSection title="Sticky Agent">
        {/* Real settings-toggle row (label left, switch right, one line) —
            not the old stacked InfoRow+bare-Checkbox layout, which put a
            whole label ABOVE a lone checkbox with nothing next to it and
            read as broken/empty space (Rahim, 2026-08-22: "fix its ui i
            dont like it"). */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5 }}>
          <Typography sx={{ fontSize: "0.8125rem", color: isAssigned ? theme.palette.text.primary : theme.palette.text.disabled }}>
            Keep this contact with the same agent
          </Typography>
          <Switch
            size="small"
            checked={!!contactInfo.alwaysAssignedEnabled}
            disabled={savingAlwaysAssigned || !isAssigned}
            onChange={(e) => handleToggleAlwaysAssigned(e.target.checked)}
          />
        </Box>
        {!isAssigned && (
          <Typography sx={{ fontSize: "0.75rem", color: theme.palette.text.secondary, fontStyle: "italic", mt: 0.75 }}>
            Only available once this contact is assigned to someone.
          </Typography>
        )}
        {contactInfo.alwaysAssignedEnabled && isAssigned && (
          <Typography sx={{ fontSize: "0.75rem", color: theme.palette.text.secondary, fontStyle: "italic", mt: 0.75 }}>
            Sticky agent: {contactInfo.assignedToName || "the current assignee"} — follows automatically if reassigned.
          </Typography>
        )}
      </DrawerSection>

      <Divider />

      {whatsappWebInfo && (
        <>
          <DrawerSection title="WhatsApp Webchat">
            <WhatsAppWebLinkActions
              status={whatsappWebInfo}
              onReset={resetWhatsappWeb}
              resetting={resettingWhatsappWeb}
              onRevoke={revokeWhatsappWeb}
              revoking={revokingWhatsappWeb}
            />
          </DrawerSection>
          <Divider />
        </>
      )}

      {/* Single add entry point now — the heading's own icon, always visible
          (not just when empty), replacing the separate "+ Add label" text
          link. The select/create form only ever opens when THAT icon is
          clicked (Rahim, 2026-08-22: "no need to use the No labels assigned
          + Add label use better add icon and when clicked on add icon only
          then [form] will open"). "No labels assigned" caption dropped too —
          an empty chip row plus a visible add icon already says that. */}
      <DrawerSection
        title="Labels"
        // Nothing to show below the heading when there are no labels and
        // the add form isn't open — the normal mb:2 heading gap just reads
        // as wasted dead space in that case (Rahim, 2026-08-22: "gap under
        // the labels... looks weird"), so trim it when there's truly
        // nothing following it.
        contentGap={contactLabels.length > 0 || labelBusy || isAddingLabel ? 0 : -1.5}
        headerAction={
          hasLabelViewPermission && LabelPermissions.Add && !isAddingLabel ? (
            <IconButton size="small" onClick={handleOpenAddLabel} sx={{ p: 0.375, color: iconColor }}>
              <IconBox size={20}><Add size="100%" color="currentColor" variant="Linear" /></IconBox>
            </IconButton>
          ) : null
        }
      >
        {!hasLabelViewPermission ? (
          <Typography sx={{ fontSize: "0.75rem", color: theme.palette.text.secondary, fontStyle: "italic" }}>
            You don't have permission to view labels
          </Typography>
        ) : (
          <>
            {(contactLabels.length > 0 || labelBusy) && (
              <Box display="flex" flexWrap="wrap" gap={0.75} mb={isAddingLabel ? 1.5 : 0}>
                {labelBusy && <CircularProgress size={16} />}
                {contactLabels.map((label) => (
                  <Chip
                    key={label}
                    size="small"
                    label={label}
                    onDelete={LabelPermissions.Delete ? () => handleRemoveLabel(label) : undefined}
                    deleteIcon={<Trash size={14} color="currentColor" variant="Linear" />}
                    disabled={removingLabel === label}
                    sx={{ bgcolor: theme.palette.background.default }}
                  />
                ))}
              </Box>
            )}
            {isAddingLabel && (
              <Box display="flex" flexDirection="column" gap={1}>
                <FormControl size="small" fullWidth>
                  <Select
                    value={selectedLabel}
                    displayEmpty
                    disabled={!!newLabelText}
                    IconComponent={SelectDropdownIcon}
                    onChange={(e) => { setSelectedLabel(e.target.value); setNewLabelText(""); }}
                  >
                    <MenuItem value="" disabled>Select existing label</MenuItem>
                    {workspaceLabelsList.map((label) => (
                      <MenuItem key={label} value={label}>{label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  placeholder="or type to create a new label"
                  value={newLabelText}
                  disabled={!!selectedLabel || hasReachedLabelsLimit}
                  inputProps={{ maxLength: 10 }}
                  onChange={(e) => { setNewLabelText(e.target.value); setSelectedLabel(""); }}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleSaveLabel(); } }}
                />
                <Box display="flex" gap={0.25} justifyContent="flex-end">
                  <IconButton size="small" onClick={() => { setIsAddingLabel(false); setNewLabelText(""); setSelectedLabel(""); }}>
                    <CloseSquare size={20} color={iconColor} variant="Linear" />
                  </IconButton>
                  <IconButton size="small" onClick={handleSaveLabel} disabled={(!newLabelText && !selectedLabel) || labelBusy}>
                    <TickSquare size={20} color={iconColor} variant="Linear" />
                  </IconButton>
                </Box>
              </Box>
            )}
          </>
        )}
      </DrawerSection>

      <Divider />

      <DrawerSection
        title="Notes"
        tint={contactNotes.length > 0 ? "#FFC107" : undefined}
        headerAction={
          NotesPermissions.Add && !isAddingNote ? (
            <IconButton size="small" onClick={() => setIsAddingNote(true)} sx={{ p: 0.375, color: iconColor }}>
              <IconBox size={20}><Add size="100%" color="currentColor" variant="Linear" /></IconBox>
            </IconButton>
          ) : null
        }
      >
        {!NotesPermissions.View ? (
          <Typography sx={{ fontSize: "0.75rem", color: theme.palette.text.secondary, fontStyle: "italic" }}>
            You don't have permission to view notes
          </Typography>
        ) : (
          <>
            {NotesPermissions.Add && isAddingNote && (
              <Box display="flex" gap={0.5} mb={1.5}>
                <TextField
                  fullWidth
                  size="small"
                  autoFocus
                  inputRef={noteInputRef}
                  placeholder="Add a note..."
                  value={newNoteText}
                  onChange={(e) => setNewNoteText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleAddNote(); }
                    else if (e.key === "Escape") { setIsAddingNote(false); setNewNoteText(""); }
                  }}
                  disabled={noteBusy || contactNotes.length >= MAX_NOTES_PER_CONTACT}
                />
                <IconButton size="small" onClick={() => { setIsAddingNote(false); setNewNoteText(""); }} disabled={noteBusy}>
                  <CloseSquare size={20} color={iconColor} variant="Linear" />
                </IconButton>
                <IconButton size="small" onClick={handleAddNote} disabled={!newNoteText.trim() || noteBusy}>
                  {noteBusy ? <CircularProgress size={16} /> : <TickSquare size={20} color={iconColor} variant="Linear" />}
                </IconButton>
              </Box>
            )}
            {/* "No notes yet" caption dropped (Rahim, 2026-08-22, same call
                as Labels' empty caption) — an empty list plus the always-
                visible add input already says that. */}
            {contactNotes.length > 0 && (
              contactNotes.map((note) => (
                <Box
                  key={note._id}
                  sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 1.75, pb: 0.75, borderBottom: `1px solid ${theme.palette.divider}` }}
                >
                  <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: theme.palette.success.main, mt: 0.75, flexShrink: 0 }} />
                  {editingNoteId === note._id ? (
                    <>
                      <TextField
                        fullWidth
                        size="small"
                        autoFocus
                        value={editingNoteText}
                        onChange={(e) => setEditingNoteText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSaveNote(note._id); }
                          else if (e.key === "Escape") { setEditingNoteId(null); setEditingNoteText(""); }
                        }}
                      />
                      <IconButton size="small" onClick={() => handleSaveNote(note._id)} disabled={noteBusy}>
                        <TickSquare size={18} color={iconColor} variant="Linear" />
                      </IconButton>
                      <IconButton size="small" onClick={() => { setEditingNoteId(null); setEditingNoteText(""); }} disabled={noteBusy}>
                        <CloseSquare size={18} color={iconColor} variant="Linear" />
                      </IconButton>
                    </>
                  ) : (
                    <>
                      <Typography sx={{ flex: 1, fontSize: "0.8125rem", wordBreak: "break-word" }}>{note.text}</Typography>
                      {NotesPermissions.Edit && (
                        <IconButton size="small" onClick={() => { setEditingNoteId(note._id); setEditingNoteText(note.text); }} disabled={noteBusy}>
                          <Edit2 size={15} color="currentColor" variant="Linear" />
                        </IconButton>
                      )}
                      {NotesPermissions.Delete && (
                        <IconButton size="small" onClick={() => handleDeleteNote(note._id)} disabled={noteBusy}>
                          <Trash size={15} color="currentColor" variant="Linear" />
                        </IconButton>
                      )}
                    </>
                  )}
                </Box>
              ))
            )}
          </>
        )}
      </DrawerSection>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Scheduled Messages section — mounted only while activeSection === "scheduled".
// The API is already contact-scoped (/scheduled-messages/contact/:id), so this
// dispatches fresh on every open (matching ChatInfo.jsx's original accordion
// behavior — pending sends can change between opens, no cache here).
function ScheduledMessagesSection({ contactId }) {
  const theme = useTheme();
  // Same dark-mode-white treatment as ContactDetailsSection's own icons.
  const iconColor = theme.palette.mode === "dark" ? "#FFFFFF" : theme.palette.text.secondary;
  const dispatch = useDispatch();
  const { guard } = useSubscriptionGuard();

  const scheduledMessages = useSelector((state) => state.scheduledMessages?.scheduledMessages || [], shallowEqual);
  const loading = useSelector((state) => state.scheduledMessages?.loading || false);
  const error = useSelector((state) => state.scheduledMessages?.error || null);

  const [cancellingId, setCancellingId] = React.useState(null);
  const [snackbar, setSnackbar] = React.useState({ open: false, message: "", severity: "success" });

  React.useEffect(() => {
    if (contactId) dispatch(fetchScheduledMessages(contactId));
  }, [dispatch, contactId]);

  const pending = scheduledMessages.filter((msg) => msg.status === "pending");

  // Per-type icon beside the badge (Rahim, 2026-08-22: "if its mp3 add mp3
  // one and so on") — audio covers mp3/ogg/etc, one glyph per messageType,
  // not per file extension.
  const MESSAGE_TYPE_ICONS = {
    text: MessageText,
    template: DocumentText1,
    image: Gallery,
    video: VideoSquare,
    document: DocumentText,
    audio: MusicSquare,
  };

  const getMessagePreview = (message) => {
    if (message.messageType === "text") return message.messageContent || "No content";
    if (message.messageType === "template") {
      return message.templateData?.name || message.templateData?.template?.name || "Template message";
    }
    if (message.mediaFileName) {
      return `${message.messageType[0].toUpperCase()}${message.messageType.slice(1)}: ${message.mediaFileName}`;
    }
    return `${message.messageType[0].toUpperCase()}${message.messageType.slice(1)} message`;
  };
  const formatScheduledTime = (dateString) => {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "Invalid Date";
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true,
    }).format(date);
  };

  const handleCancel = async (messageId) => {
    if (!(await guard([]))) return;
    setCancellingId(messageId);
    try {
      await dispatch(cancelScheduledMessage(messageId)).unwrap();
      setSnackbar({ open: true, message: "Scheduled message cancelled", severity: "success" });
      if (contactId) dispatch(fetchScheduledMessages(contactId));
    } catch (err) {
      if (!isSubscriptionBlockedError(err)) {
        setSnackbar({ open: true, message: err || "Failed to cancel scheduled message", severity: "error" });
      }
    } finally {
      setCancellingId(null);
    }
  };

  return (
    <Box sx={{ p: 1.5, height: "100%" }}>
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} sx={{ width: "100%" }} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {loading ? (
        <ScheduledMessagesSkeleton />
      ) : error ? (
        <Typography sx={{ fontSize: "0.8125rem", color: theme.palette.error.main, textAlign: "center" }}>
          Error loading scheduled messages: {error}
        </Typography>
      ) : pending.length === 0 ? (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%" }}>
          <Box sx={{ mb: 1.5, color: theme.palette.text.secondary, display: "flex" }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none">
              <g>
                <path d="M8 5.75C7.59 5.75 7.25 5.41 7.25 5V2C7.25 1.59 7.59 1.25 8 1.25C8.41 1.25 8.75 1.59 8.75 2V5C8.75 5.41 8.41 5.75 8 5.75Z" fill="currentColor" />
                <path d="M16 5.75C15.59 5.75 15.25 5.41 15.25 5V2C15.25 1.59 15.59 1.25 16 1.25C16.41 1.25 16.75 1.59 16.75 2V5C16.75 5.41 16.41 5.75 16 5.75Z" fill="currentColor" />
                <path d="M8.5 14.5009C8.37 14.5009 8.24 14.4709 8.12 14.4209C7.99 14.3709 7.89001 14.3009 7.79001 14.2109C7.61001 14.0209 7.5 13.7609 7.5 13.5009C7.5 13.2409 7.61001 12.9809 7.79001 12.7909C7.89001 12.7009 8 12.6309 8.12 12.5809C8.36 12.4809 8.64 12.4809 8.88 12.5809C9 12.6309 9.10999 12.7009 9.20999 12.7909C9.24999 12.8409 9.29999 12.8909 9.32999 12.9409C9.36999 13.0009 9.40001 13.0609 9.42001 13.1209C9.45001 13.1809 9.47001 13.2409 9.48001 13.3009C9.49001 13.3709 9.5 13.4409 9.5 13.5009C9.5 13.7609 9.38999 14.0209 9.20999 14.2109C9.10999 14.3009 9 14.3709 8.88 14.4209C8.76 14.4709 8.63 14.5009 8.5 14.5009Z" fill="currentColor" />
                <path d="M12 14.4992C11.87 14.4992 11.74 14.4692 11.62 14.4192C11.49 14.3692 11.39 14.2992 11.29 14.2092C11.11 14.0192 11 13.7592 11 13.4992C11 13.4392 11.01 13.3692 11.02 13.2992C11.03 13.2392 11.05 13.1792 11.08 13.1192C11.1 13.0592 11.13 12.9992 11.17 12.9392C11.21 12.8892 11.25 12.8392 11.29 12.7892C11.66 12.4192 12.33 12.4192 12.71 12.7892C12.75 12.8392 12.79 12.8892 12.83 12.9392C12.87 12.9992 12.9 13.0592 12.92 13.1192C12.95 13.1792 12.97 13.2392 12.98 13.2992C12.99 13.3692 13 13.4392 13 13.4992C13 13.7592 12.89 14.0192 12.71 14.2092C12.52 14.3892 12.27 14.4992 12 14.4992Z" fill="currentColor" />
                <path d="M8.5 18.0009C8.37 18.0009 8.24 17.9709 8.12 17.9209C8 17.8709 7.89001 17.8009 7.79001 17.7109C7.61001 17.5209 7.5 17.2609 7.5 17.0009C7.5 16.9409 7.50999 16.8709 7.51999 16.8109C7.52999 16.7409 7.54999 16.6809 7.57999 16.6209C7.59999 16.5609 7.63001 16.5009 7.67001 16.4409C7.70001 16.3909 7.75001 16.3409 7.79001 16.2909C7.89001 16.2009 8 16.1309 8.12 16.0809C8.36 15.9809 8.64 15.9809 8.88 16.0809C9 16.1309 9.10999 16.2009 9.20999 16.2909C9.24999 16.3409 9.29999 16.3909 9.32999 16.4409C9.36999 16.5009 9.40001 16.5609 9.42001 16.6209C9.45001 16.6809 9.47001 16.7409 9.48001 16.8109C9.49001 16.8709 9.5 16.9409 9.5 17.0009C9.5 17.2609 9.38999 17.5209 9.20999 17.7109C9.10999 17.8009 9 17.8709 8.88 17.9209C8.76 17.9709 8.63 18.0009 8.5 18.0009Z" fill="currentColor" />
                <path d="M20.5 9.83984H3.5C3.09 9.83984 2.75 9.49984 2.75 9.08984C2.75 8.67984 3.09 8.33984 3.5 8.33984H20.5C20.91 8.33984 21.25 8.67984 21.25 9.08984C21.25 9.49984 20.91 9.83984 20.5 9.83984Z" fill="currentColor" />
                <path d="M15.8201 22.7796C15.4401 22.7796 15.0801 22.6396 14.8201 22.3796C14.5101 22.0696 14.3701 21.6196 14.4401 21.1496L14.6301 19.7996C14.6801 19.4496 14.8901 19.0296 15.1401 18.7796L18.68 15.2396C19.16 14.7596 19.6301 14.5096 20.1401 14.4596C20.7701 14.3996 21.38 14.6596 21.96 15.2396C22.57 15.8496 23.39 17.0896 21.96 18.5196L18.4201 22.0596C18.1701 22.3096 17.75 22.5196 17.4 22.5696L16.05 22.7596C15.97 22.7696 15.9001 22.7796 15.8201 22.7796ZM20.31 15.9496C20.3 15.9496 20.29 15.9496 20.28 15.9496C20.14 15.9596 19.95 16.0896 19.74 16.2996L16.2001 19.8396C16.1701 19.8696 16.12 19.9696 16.12 20.0096L15.9401 21.2596L17.1901 21.0796C17.2301 21.0696 17.33 21.0196 17.36 20.9896L20.9 17.4496C21.34 17.0096 21.4 16.7896 20.9 16.2896C20.74 16.1396 20.51 15.9496 20.31 15.9496Z" fill="currentColor" />
                <path d="M20.9201 19.2509C20.8501 19.2509 20.7801 19.2409 20.7201 19.2209C19.4001 18.8509 18.3501 17.8009 17.9801 16.4809C17.8701 16.0809 18.1001 15.6709 18.5001 15.5509C18.9001 15.4409 19.3101 15.6709 19.4301 16.0709C19.6601 16.8909 20.3101 17.5409 21.1301 17.7709C21.5301 17.8809 21.7601 18.3009 21.6501 18.7009C21.5501 19.0309 21.2501 19.2509 20.9201 19.2509Z" fill="currentColor" />
                <path d="M12 22.75H8C4.35 22.75 2.25 20.65 2.25 17V8.5C2.25 4.85 4.35 2.75 8 2.75H16C19.65 2.75 21.75 4.85 21.75 8.5V12C21.75 12.41 21.41 12.75 21 12.75C20.59 12.75 20.25 12.41 20.25 12V8.5C20.25 5.64 18.86 4.25 16 4.25H8C5.14 4.25 3.75 5.64 3.75 8.5V17C3.75 19.86 5.14 21.25 8 21.25H12C12.41 21.25 12.75 21.59 12.75 22C12.75 22.41 12.41 22.75 12 22.75Z" fill="currentColor" />
              </g>
            </svg>
          </Box>
          <Typography variant="h6" color="text.secondary">
            No scheduled messages
          </Typography>
        </Box>
      ) : (
        <Box display="flex" flexDirection="column" gap={1.75}>
          {pending.map((message) => (
            <Box key={message._id} sx={{ p: 1.75, border: `1px solid ${theme.palette.divider}`, borderRadius: 2, bgcolor: theme.palette.background.default }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1.5}>
                <Box flex={1} sx={{ minWidth: 0 }}>
                  <Box display="flex" justifyContent="flex-start" mb={1}>
                    {/* Same neutral bordered-square badge used for the
                        Status/Expires-in pills elsewhere in this drawer —
                        not a filled color pill (Rahim, 2026-08-22: "fix the
                        template pill its not looking good use the same
                        background less square type box we used"). Explicit
                        inline-flex + alignSelf so it shrink-wraps its text
                        exactly, no stray left inset from stretching to the
                        row (Rahim, 2026-08-22: "there is sapce at the left
                        side... allign left"). */}
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "4px",
                        alignSelf: "flex-start",
                        borderRadius: "6px",
                        bgcolor: theme.palette.background.paper,
                        border: `1px solid ${theme.palette.divider}`,
                        px: "6px",
                        py: "2px",
                      }}
                    >
                      {MESSAGE_TYPE_ICONS[message.messageType] && (
                        <IconBox size={12}>
                          {React.createElement(MESSAGE_TYPE_ICONS[message.messageType], {
                            size: "100%",
                            color: iconColor,
                            variant: "Linear",
                          })}
                        </IconBox>
                      )}
                      <Typography sx={{ fontSize: "0.6875rem", fontWeight: 500, color: theme.palette.text.primary, whiteSpace: "nowrap" }}>
                        {message.messageType.charAt(0).toUpperCase() + message.messageType.slice(1)}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography sx={{ fontSize: "0.8125rem", mb: 1, wordBreak: "break-word" }}>{getMessagePreview(message)}</Typography>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <IconBox size={14}><Clock size="100%" color={iconColor} variant="Linear" /></IconBox>
                    <Typography sx={{ fontSize: "0.7rem", color: theme.palette.text.secondary }}>
                      {formatScheduledTime(message.scheduledTime)}
                    </Typography>
                  </Box>
                </Box>
                <IconButton size="small" onClick={() => handleCancel(message._id)} disabled={cancellingId === message._id} sx={{ color: iconColor }}>
                  {cancellingId === message._id ? <CircularProgress size={16} /> : <CloseSquare size={20} color="currentColor" variant="Linear" />}
                </IconButton>
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Group Description section (WhatsApp Groups plan follow-up, 2026-09-26) -- the group
// counterpart of ContactDetailsSection above, deliberately far simpler: no labels/notes/
// sticky-agent/editable fields, none of which apply to a group (decision #2/#4). groupInfo
// itself needs no fetch here -- same reasoning ContactDetailsSection's own comment gives for
// contactInfo: useContactSelection.js's selectGroup already fetches it (fetchGroupById) the
// moment a group is opened, so it's already resident in Redux by the time this can be
// clicked open. Participant list/add-remove already live in ChatboxNavbar.jsx's own "N
// members" popover -- this section is just the description text, not a second members list.
function GroupDescriptionSection({ groupId }) {
  const theme = useTheme();
  const iconColor = theme.palette.mode === "dark" ? "#FFFFFF" : theme.palette.text.secondary;
  const dispatch = useDispatch();
  const { guard } = useSubscriptionGuard();
  const groupInfo = useSelector((state) => state.inboxInfo?.groupInfo);
  const isLoading = useSelector(selectInboxInfoLoading);

  // Edit toggle (2026-09-26 follow-up) -- same one-button-arms-edit-mode shape
  // ContactDetailsSection's own Profile section already uses, just for the single description
  // field instead of Name/Business name/Email.
  const [editMode, setEditMode] = React.useState(false);
  const [descriptionDraft, setDescriptionDraft] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [snackbar, setSnackbar] = React.useState({ open: false, message: "", severity: "success" });

  // Group name (subject) edit -- a second, independent edit toggle in the same header block
  // (2026-09-26 follow-up). Kept separate from the description's own editMode/saving state
  // rather than sharing it: the two fields save via two different endpoints/thunks and can be
  // edited independently of one another.
  const [nameEditMode, setNameEditMode] = React.useState(false);
  const [nameDraft, setNameDraft] = React.useState("");
  const [nameSaving, setNameSaving] = React.useState(false);

  const startEdit = () => {
    setDescriptionDraft(groupInfo?.description || "");
    setEditMode(true);
  };

  const handleSave = async () => {
    if (!groupInfo?.groupID || saving) return;
    if (!(await guard([]))) return;
    setSaving(true);
    try {
      await dispatch(updateGroupDescription({ groupId: groupInfo.groupID, description: descriptionDraft.trim() })).unwrap();
      setEditMode(false);
      setSnackbar({ open: true, message: "Group description updated", severity: "success" });
    } catch (err) {
      setSnackbar({ open: true, message: typeof err === "string" ? err : "Failed to update description", severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  const startNameEdit = () => {
    setNameDraft(groupInfo?.subject || groupInfo?.name || "");
    setNameEditMode(true);
  };

  const handleSaveName = async () => {
    const trimmed = nameDraft.trim();
    if (!groupInfo?.groupID || nameSaving || !trimmed) return;
    if (!(await guard([]))) return;
    setNameSaving(true);
    try {
      await dispatch(updateGroupSubject({ groupId: groupInfo.groupID, subject: trimmed })).unwrap();
      setNameEditMode(false);
      setSnackbar({ open: true, message: "Group name updated", severity: "success" });
    } catch (err) {
      setSnackbar({ open: true, message: typeof err === "string" ? err : "Failed to update group name", severity: "error" });
    } finally {
      setNameSaving(false);
    }
  };

  if (isLoading && !groupInfo) {
    return (
      <Box sx={{ p: 2.25 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
          <Skeleton variant="circular" width={54} height={54} sx={{ flexShrink: 0 }} />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Skeleton variant="text" width="60%" height={20} />
            <Skeleton variant="text" width="40%" height={14} />
          </Box>
        </Box>
        <Skeleton variant="text" width="30%" height={16} sx={{ mb: 1 }} />
        <Skeleton variant="text" width="90%" height={14} />
        <Skeleton variant="text" width="70%" height={14} />
      </Box>
    );
  }
  if (!groupInfo || (groupId && String(groupInfo.groupID) !== String(groupId))) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography sx={{ fontSize: "0.8125rem", color: theme.palette.text.secondary, textAlign: "center" }}>
          No group selected.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} sx={{ width: "100%" }} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Header card -- same shape as ContactDetailsSection's own (avatar left, name +
          member-count to its right), just a subject-derived placeholder avatar instead of a
          real photo (no group icon fetch exists in this codebase, per the plan's own
          disclosed, deliberate deferral -- see GroupModel.iconUrl's own comment). */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, pl: "18px", pr: "16px", py: 2.25 }}>
        <Avatar
          src={nameToContactAvatarImage(groupInfo.subject || groupInfo.name)}
          imgProps={NON_DRAGGABLE_AVATAR_IMG_PROPS}
          sx={{
            width: 54,
            height: 54,
            bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.05)",
            border: `1.5px solid ${theme.palette.divider}`,
          }}
        />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          {nameEditMode ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <TextField
                fullWidth
                size="small"
                autoFocus
                placeholder="Group name"
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                disabled={nameSaving}
                onKeyDown={(e) => {
                  if (e.key === "Escape") setNameEditMode(false);
                  if (e.key === "Enter") handleSaveName();
                }}
              />
              {nameSaving ? (
                <CircularProgress size={16} sx={{ flexShrink: 0 }} />
              ) : (
                <>
                  <IconButton size="small" onClick={handleSaveName} disabled={!nameDraft.trim()}>
                    <TickCircle size={18} color="currentColor" variant="Bold" />
                  </IconButton>
                  <IconButton size="small" onClick={() => setNameEditMode(false)}>
                    <CloseSquare size={18} color={iconColor} variant="Linear" />
                  </IconButton>
                </>
              )}
            </Box>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography
                title={groupInfo.subject || groupInfo.name || "Unknown Group"}
                sx={{ fontSize: "0.9375rem", fontWeight: 600, color: theme.palette.text.primary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
              >
                {groupInfo.subject || groupInfo.name || "Unknown Group"}
              </Typography>
              <IconButtonWithTooltip label="Edit group name" size="small" onClick={startNameEdit} sx={{ flexShrink: 0 }}>
                <Edit2 size={14} color={iconColor} variant="Linear" />
              </IconButtonWithTooltip>
            </Box>
          )}
          <Typography sx={{ fontSize: "0.75rem", color: theme.palette.text.secondary }}>
            {groupInfo.participantCount ?? (groupInfo.participants || []).length} members
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ my: 1 }} />

      <Box sx={{ pl: "18px", pr: "12px", py: 1.5 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1.25 }}>
          <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600, color: theme.palette.text.primary }}>
            Description
          </Typography>
          {/* Deliberately not gated on isSelfAdmin (see updateGroupDescription controller's own
              comment) -- whether editing is actually allowed is a per-group WhatsApp setting
              this codebase doesn't currently capture, so this is always offered and a real
              rejection just surfaces via the snackbar. */}
          <Box
            onClick={saving ? undefined : editMode ? handleSave : startEdit}
            sx={{
              display: "flex", alignItems: "center", gap: 0.5,
              cursor: saving ? "default" : "pointer",
              color: theme.palette.text.secondary,
              "&:hover": { color: theme.palette.text.primary },
            }}
          >
            {saving ? (
              <CircularProgress size={13} />
            ) : (
              <IconBox size={14}>
                {editMode
                  ? <TickCircle size="100%" color="currentColor" variant="Bold" />
                  : <Edit2 size="100%" color="currentColor" variant="Linear" />}
              </IconBox>
            )}
            <Typography sx={{ fontSize: "0.8125rem", fontWeight: 500, color: "inherit" }}>
              {editMode ? "Save" : "Edit"}
            </Typography>
          </Box>
        </Box>

        {editMode ? (
          <>
            <TextField
              fullWidth
              multiline
              minRows={3}
              maxRows={8}
              autoFocus
              placeholder="Add a group description…"
              value={descriptionDraft}
              onChange={(e) => setDescriptionDraft(e.target.value)}
              disabled={saving}
              onKeyDown={(e) => { if (e.key === "Escape") setEditMode(false); }}
            />
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
              <IconButton size="small" onClick={() => setEditMode(false)} disabled={saving}>
                <CloseSquare size={20} color={iconColor} variant="Linear" />
              </IconButton>
            </Box>
          </>
        ) : (
          <Typography
            sx={{
              fontSize: "0.875rem",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              color: groupInfo.description ? theme.palette.text.primary : theme.palette.text.secondary,
              fontStyle: groupInfo.description ? "normal" : "italic",
            }}
          >
            {groupInfo.description || "No description set for this group."}
          </Typography>
        )}
      </Box>

      {/* Tier 3 (2026-09-29): invite link / member-add mode / join requests / leave group. */}
      <GroupAdminActions />
    </Box>
  );
}

const ChatInfoRail = () => {
  const theme = useTheme();
  // Same dark-mode-white treatment as the section components' own icons.
  const iconColor = theme.palette.mode === "dark" ? "#FFFFFF" : theme.palette.text.secondary;
  const contactId = useSelector(selectSelectedContactID);
  const screenSize = useSelector((state) => state.inbox.screenSize);
  const drawerMaxWidth = screenSize === "lg" ? DRAWER_MAX_WIDTH_LG : DRAWER_MAX_WIDTH_XL;
  // Same isGroup source ChatboxNavbar.jsx itself uses (messages.isGroup) -- "Contact details"
  // is swapped for "Group description" when a group thread is open (2026-09-26 follow-up):
  // contactInfo is always null for a group (selectGroup clears it, never repopulates), so that
  // section used to just show "No contact selected." for one, a dead end.
  const isGroup = useSelector((state) => !!state.inboxMessage?.messages?.isGroup);
  const activeRailItems = isGroup ? GROUP_RAIL_ITEMS : RAIL_ITEMS;
  // null | "profile" | "groupDescription" | "scheduled" — one panel open at a time, not two
  // independent flags, so opening one always closes the other (Rahim,
  // 2026-08-21: "like as each drawer depends upon if it was clicked or not").
  const [activeSection, setActiveSection] = React.useState(null);

  const handleIconClick = (key) => {
    setActiveSection((current) => (current === key ? null : key));
  };

  // Website chat has no scheduled sends (no template/catalog send either), so its
  // Scheduled tab is dropped; a stale "scheduled" selection resolves to no active item.
  // Switching between a group and a 1:1 contact collapses an open panel on its own: the active
  // key is no longer in railItems, so activeItem resolves to null.
  const railContactInfo = useSelector(selectContactInfo);
  const isWebsiteContact = railContactInfo?.platform === "website";
  // Only a Shopify customer gets the Shopify icon (never a group, never anyone else): appended
  // after "Scheduled messages". If the open contact stops being a Shopify customer, the key drops
  // out of railItems and an open Shopify panel collapses on its own, like any other stale key.
  const isShopifyContact = !isGroup && !!railContactInfo?.shopifyCustomerId;
  const baseRailItems = isWebsiteContact ? activeRailItems.filter((item) => item.key !== "scheduled") : activeRailItems;
  const railItems = isShopifyContact ? [...baseRailItems, SHOPIFY_RAIL_ITEM] : baseRailItems;
  const activeItem = railItems.find((item) => item.key === activeSection) || null;

  // Drag-resizable, same pattern as InboxAll's own panel in Inbox.jsx —
  // persisted width, live drag via mousemove/mouseup on document (not just
  // the handle itself, so a fast drag doesn't outrun the cursor), saved to
  // localStorage only once the drag ends.
  const [drawerWidth, setDrawerWidth] = React.useState(() => {
    if (typeof window === "undefined") return DRAWER_DEFAULT_WIDTH;
    const stored = parseInt(window.localStorage.getItem(DRAWER_WIDTH_STORAGE_KEY), 10);
    return Number.isFinite(stored) && stored >= DRAWER_MIN_WIDTH && stored <= DRAWER_MAX_WIDTH_XL ? stored : DRAWER_DEFAULT_WIDTH;
  });
  const isResizingRef = React.useRef(false);

  const handleResizeStart = React.useCallback((e) => {
    e.preventDefault();
    isResizingRef.current = true;
    const startX = e.clientX;
    const startWidth = drawerWidth;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    const handleMouseMove = (moveEvent) => {
      if (!isResizingRef.current) return;
      // Inverted vs InboxAll's own +delta — this panel is anchored on its
      // RIGHT edge (against the rail) and grows LEFTWARD, so dragging the
      // handle left (negative clientX delta) should widen it, not narrow it.
      const next = Math.min(
        drawerMaxWidth,
        Math.max(DRAWER_MIN_WIDTH, Math.round(startWidth - (moveEvent.clientX - startX)))
      );
      setDrawerWidth(next);
    };
    const handleMouseUp = () => {
      isResizingRef.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      setDrawerWidth((current) => {
        window.localStorage.setItem(DRAWER_WIDTH_STORAGE_KEY, String(current));
        return current;
      });
    };
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  }, [drawerWidth, drawerMaxWidth]);

  return (
    <>
      {/* Collapse (horizontal), not an absolutely-positioned overlay —
          pushes/reflows ChatBox instead of covering it, same proven pattern
          the original ChatInfo.jsx panel and InboxSettings.jsx's own Collapse
          panel already use in this app. Rendered BEFORE the rail Box below so
          it sits between ChatBox and the rail, not on top of either (Rahim,
          2026-08-21: "it will open inside the chat panel making the inner
          chat component moving with it... is this a good practice or not" —
          confirmed yes, audited, no risk found). */}
      <Collapse in={Boolean(activeItem)} orientation="horizontal" timeout={300} sx={{ height: "100%", flexShrink: 0 }}>
        <Box
          sx={{
            // Re-clamped against drawerMaxWidth here too, not just at drag time — a
            // width stored while on XL (up to 560px) must not carry straight over if
            // the window is then resized down to LG.
            width: `${Math.min(drawerWidth, drawerMaxWidth)}px`,
            height: "100%",
            display: "flex",
            flexDirection: "column",
            position: "relative",
            // Same tokens the rail itself uses, not .paper — reads as one
            // continuous surface with the rail instead of a visually
            // different floating panel (Rahim, 2026-08-21: "use the same
            // background colour as the rail and use border colour as the
            // rail").
            bgcolor: theme.palette.background.default,
            borderLeft: `1px solid ${theme.palette.divider}`,
          }}
        >
          {/* Drag handle — same invisible-overlay-on-existing-border approach
              InboxAll.jsx's own resize strip uses, not a separate sibling
              element (that class of bug already found once this session:
              a separate strip physically splitting a border line in two). */}
          <Box
            onMouseDown={handleResizeStart}
            sx={{
              position: "absolute",
              top: 0,
              bottom: 0,
              left: 0,
              width: "4px",
              cursor: "col-resize",
              zIndex: 2,
              "&:hover": {
                borderLeft: (t) => `2px solid ${t.palette.primary.main}`,
              },
            }}
          />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              pl: "18px",
              pr: "12px",
              py: 1.25,
              borderBottom: `1px solid ${theme.palette.divider}`,
              flexShrink: 0,
            }}
          >
            {/* Same icon + same click-to-close pattern InboxSettings.jsx's own
                panel-heading toggle uses (SidebarLeft, always Linear/outline —
                never filled solid regardless of state, per that file's own
                explicit rule) — not a different icon just because this is a
                different panel (Rahim, 2026-08-21: "add the icon same as the
                inbox one"). */}
            <IconButton
              size="small"
              onClick={() => setActiveSection(null)}
              aria-label="Close panel"
              sx={{
                color: iconColor,
                "&:hover": { color: theme.palette.mode === "dark" ? "#FFFFFF" : theme.palette.text.primary },
              }}
            >
              <SidebarToggleIconIx size="22" color="currentColor" variant="Linear" />
            </IconButton>
            <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600, color: theme.palette.text.primary }}>
              {activeItem?.title}
            </Typography>
          </Box>

          {/* Body — mounts only the active section's component, so its data
              fetch (fetchLabels / fetchScheduledMessages) fires exactly once
              per open, gated by the click that opened it (per the
              2026-08-21 audit) rather than by mount of ChatInfoRail itself. */}
          <Box sx={{ flex: 1, overflowY: "auto", minHeight: 0, ...slimScrollbarSx }}>
            {activeItem?.key === "profile" && <ContactDetailsSection contactId={contactId} />}
            {activeItem?.key === "groupDescription" && <GroupDescriptionSection groupId={contactId} />}
            {activeItem?.key === "scheduled" && <ScheduledMessagesSection contactId={contactId} />}
            {activeItem?.key === "shopify" && (
              <ShopifyOrdersPanel contactId={contactId} shopifyCustomerId={railContactInfo?.shopifyCustomerId} />
            )}
          </Box>
        </Box>
      </Collapse>

      <Box
        sx={{
          width: `${RAIL_WIDTH}px`,
          height: "100%",
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 0.75,
          pt: 1.5,
          boxSizing: "border-box",
          // Same tokens ChatboxNavbar.jsx itself uses (background.default,
          // divider) — not just a similar color, the literal same ones, so
          // this reads as one continuous surface with the real navbar above
          // it (Rahim, 2026-08-21).
          bgcolor: theme.palette.background.default,
          borderLeft: `1px solid ${theme.palette.divider}`,
        }}
      >
        {railItems.map(({ key, Icon, label }) => {
          const isActive = activeSection === key;
          return (
              <IconButtonWithTooltip
                key={key}
                label={label}
                placement="left"
                onClick={() => handleIconClick(key)}
                aria-pressed={isActive}
                aria-label={label}
                disableRipple
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "10px",
                  // Always-visible outline, not just a hover-only highlight —
                  // matches the reference's bordered-square look (Rahim,
                  // 2026-08-21). Active section marked neutrally (darker
                  // border/icon + the same rail-hover tint, not green — Rahim,
                  // 2026-08-21: "who asked you to make the icon green").
                  border: `1px solid ${theme.palette.divider}`,
                  // Dark mode: white for both states (hollow outline when
                  // inactive via the Linear variant below, filled white when
                  // active via Bold) instead of grey — Rahim, 2026-08-22:
                  // "when the user doesnt click its hollow when clicked it
                  // gets white filled". Light mode unchanged.
                  color: theme.palette.mode === "dark"
                    ? "#FFFFFF"
                    : isActive ? theme.palette.text.primary : theme.palette.text.secondary,
                  bgcolor: isActive
                    ? theme.palette.mode === "dark" ? "rgba(255,255,255,0.055)" : "rgba(0,0,0,0.045)"
                    : "transparent",
                  transition: "background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease",
                  "&:hover": {
                    backgroundColor: isActive
                      ? undefined
                      : theme.palette.mode === "dark" ? "rgba(255,255,255,0.055)" : "rgba(0,0,0,0.045)",
                    color: theme.palette.mode === "dark" ? "#FFFFFF" : theme.palette.text.primary,
                  },
                }}
              >
                <Box sx={{ width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Icon size="100%" color="currentColor" variant={isActive ? "Bold" : "Linear"} />
                </Box>
              </IconButtonWithTooltip>
          );
        })}
      </Box>

    </>
  );
};

// Not memoized — this reads useSelector(selectSelectedContactID) itself
// (needed so ContactDetailsSection/ScheduledMessagesSection know which
// contact to fetch for), so it legitimately re-renders when the selected
// contact changes. The two section components above still only re-render
// while their own section is open (they're unmounted otherwise).
// Named export (alongside the default) so GroupInfoDrawer.jsx (the SM/MD/XS drawer's own group
// counterpart to ChatInfo.jsx) can reuse this exact section rather than duplicating its
// view/edit logic a second time -- the rail's own header (title + close icon) isn't part of
// this component, so it drops cleanly into a drawer with its own separate header too.
export { GroupDescriptionSection };
export default ChatInfoRail;
